import * as React from 'react';
import Button from '@mui/material/Button';
import { Avatar, Box, Card, CardActions, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Pagination, Select, Stack, Switch, TextField, Tooltip, Typography, debounce, makeStyles, Paper, Chip, Tabs, Tab } from '@mui/material';
import { approveAccountDeletionRequest, assignUserIdToDeletionRequest, cancelOffer, getAccountDeletionRequests, getAISupportConfiguration, getChatMessagesById, getFunnelList, getOffers, getPromoBanners, getSupportChatCategories, getSupportChatsPaginated, getTemplates, getUsersPaginated, rejectAccountDeletionRequest } from '../Api';
import { useNavigate } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PromoBannerDto from '../models/dtos/PromoBannerDto';
import PromoBannerCard from '../components/PromoBannerCard';
import TemplateDto from '../models/dtos/TemplateDto';
import { DataGrid } from '@mui/x-data-grid/DataGrid/DataGrid';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import FunnelDto from '../models/dtos/FunnelDto';
import FunnelConfigDto from '../models/dtos/FunnelConfigDto';
import { GridCellParams, GridValueGetterParams } from '@mui/x-data-grid/models/params/gridCellParams';
import { useCallback } from 'react';
import AccountDeletionRequestDto from '../models/dtos/AccountDeletionRequestDto';
import { rowSelectionStateInitializer } from '@mui/x-data-grid/internals';
import AccountMainInfoDto from '../models/dtos/AccountMainInfoDto';
import SubscriptionTierChip from '../components/SubscriptionTierChip';
import SupportMessageWithCommentCountDto from '../models/dtos/SupportMessageWithCommentCountDto';
import SupportChatDto from '../models/dtos/SupportChatDto';
import { Link } from 'react-router-dom';
import AISupportConfigModal from '../components/AISupportConfigModal';
import AISupportConfigurationDto from '../models/dtos/AISupportConfigurationDto';
import AiAgentStatsPanel from '../components/ai/AiAgentStatsPanel';
import SupportChatCategoryDto from '../models/dtos/SupportChatCategoryDto';
import { describeSupportMessage, SupportMessagePreviewText } from '../components/SupportPhotos';

export const formatRevenue = (revenue: number) => {
    return revenue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    });
}

const getVisibleTags = (tags: string[]) => tags.slice(0, 2);

const getHiddenTags = (tags: string[]) => tags.slice(2);

export default function SupportPage() {
    let defaultLimit = 100;
    const [state, setState] = React.useState({
        loaded: false,
        currentPage: 0,
        offset: 0,
        limit: defaultLimit,
        totalPages: 0,
        chats: [] as SupportChatDto[],
        showOnlyPriority: false,
        showOnlyActive: false
    });

    const [categories, setCategories] = React.useState<SupportChatCategoryDto[]>([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = React.useState<string[]>([]);
    const [categoriesLoading, setCategoriesLoading] = React.useState(false);

    const [configModalOpen, setConfigModalOpen] = React.useState(false);
    const [aiConfig, setAiConfig] = React.useState<AISupportConfigurationDto | null>(null);
    const [configLoading, setConfigLoading] = React.useState(false);

    const navigate = useNavigate();

    const onMessageClicked = async (userId: number) => {
        navigate('/users/' + userId + '?tab=chat');
    }

    const onMessageRead = async (userId: number, chatId: number) => {
        let result = await getChatMessagesById(userId, chatId, 0, 100);
        if (result) {
            let chat = state.chats.find(c => c.correspondentId == userId);
            if (chat) {
                chat.read = true;
                setState(prevState => ({ ...prevState, chats: state.chats }));
            }
        }
    }

    const columns: GridColDef[] = [
        {
            field: 'priority', headerName: '⚠️', width: 60, renderCell: (params: GridCellParams) =>
                <React.Fragment>
                    {params.row.priority && <span style={{ fontSize: '32px', color: 'red', fontWeight: 'bold' }}>❗</span>}
                </React.Fragment>
        },
        {
            field: 'isPurchaser', headerName: '💰', width: 60, renderCell: (params: GridCellParams) =>
                <React.Fragment>
                    {params.row.isPurchaser && <span style={{ fontSize: '28px', color: '#4CAF50', fontWeight: 'bold' }}>💵</span>}
                </React.Fragment>
        },
        {
            field: 'subscriptionTier', headerName: 'Tier', width: 90, sortable: false, renderCell: (params: GridCellParams) =>
                <SubscriptionTierChip tier={params.row.subscriptionTier} />
        },
        {
            field: 'osType', headerName: 'OS', width: 64, renderCell: (params: GridCellParams) =>
                <React.Fragment>
                    {params.row.osType && <img src={params.row.osType.includes('android') ? '/android_logo.png' : '/ios_logo.png'} width={24} height={24} />}
                </React.Fragment>
        },
        {
            field: 'status', headerName: 'Status', width: 100, renderCell: (params: GridCellParams) =>
                params.row.active
                    ? <Chip label="Active" size="small" color="success" sx={{ fontWeight: 'bold' }} />
                    : <Chip label="Closed" size="small" icon={<LockOutlinedIcon />}
                        sx={{ fontWeight: 'bold', fontStyle: 'normal', bgcolor: '#757575', color: 'white', '& .MuiChip-icon': { color: 'white' } }} />
        },
        { field: 'correspondentId', headerName: 'userId', width: 128 },
        {
            field: 'locale', headerName: 'Locale', width: 90, sortable: false, renderCell: (params: GridCellParams) =>
                <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ width: '100%', opacity: params.row.active ? 1 : 0.6 }}>
                    {params.row.country ? (
                        <Tooltip title={params.row.country.name} arrow>
                            <img src={params.row.country.icon} width={24} height={24} style={{ cursor: 'pointer' }} />
                        </Tooltip>
                    ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                    <Typography variant="caption" sx={{ textTransform: 'uppercase', lineHeight: 1 }}>
                        {params.row.language || '—'}
                    </Typography>
                </Stack>
        },
        {
            field: 'user', headerName: 'User', width: 300, renderCell: (params: GridCellParams) =>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                        src={params.row.photoUrl}
                        sx={{ 
                            width: 40, 
                            height: 40,
                            opacity: params.row.active ? 1 : 0.5
                        }}
                    />
                    <Typography sx={{ opacity: params.row.active ? 1 : 0.6 }}>
                        {params.row.correspondentName}
                    </Typography>
                </Box>
        },
        { 
            field: 'tags',
            headerName: 'Tags',
            width: 220,
            sortable: false,
            renderCell: (params: GridCellParams) => {
                const tags = (params.row.tags as string[]) ?? [];
                const visibleTags = getVisibleTags(tags);
                const hiddenTags = getHiddenTags(tags);

                return (
                    <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ py: 0.5, opacity: params.row.active ? 1 : 0.6 }}>
                        {visibleTags.map(tag => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))}
                        {hiddenTags.length > 0 && (
                            <Tooltip
                                title={
                                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                                        {hiddenTags.map((tag: string) => (
                                            <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ color: 'white', borderColor: 'white' }} />
                                        ))}
                                    </Stack>
                                }
                                arrow
                            >
                                <Chip label={`+${hiddenTags.length}`} size="small" variant="filled" />
                            </Tooltip>
                        )}
                        {tags.length === 0 && (
                            <Typography variant="body2" color="text.secondary">No tags</Typography>
                        )}
                    </Stack>
                );
            }
        },
        { 
            field: 'message', 
            headerName: 'Message', 
            width: 420,
            renderCell: (params: GridCellParams) =>
                <Stack spacing={0.5} sx={{ py: 0.75, opacity: params.row.active ? 1 : 0.6, width: '100%' }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {params.row.chatName || 'Untitled ticket'}
                        </Typography>
                        {params.row.category ? (
                            <Chip
                                label={params.row.category.name}
                                size="small"
                                color="default"
                                variant="outlined"
                            />
                        ) : (
                            <Chip
                                label="No category"
                                size="small"
                                color="default"
                                variant="outlined"
                            />
                        )}
                    </Stack>
                    {/* Photo and share blocks read as icons, never as their JSON (SupportPhotos.tsx). */}
                    <Tooltip title={describeSupportMessage(params.row.lastMessageText) || 'No message'} arrow>
                        <Box>
                            <SupportMessagePreviewText text={params.row.lastMessageText} />
                        </Box>
                    </Tooltip>
                </Stack>
        },
        {
            field: 'lastMessageDate', 
            headerName: 'Date', 
            width: 200, 
            valueGetter: (params: GridValueGetterParams) =>
                new Date(params.row.lastMessageDate).toLocaleString(),
            renderCell: (params: GridCellParams) =>
                <Typography sx={{ opacity: params.row.active ? 1 : 0.6 }}>
                    {new Date(params.row.lastMessageDate).toLocaleString()}
                </Typography>
        },
        {
            field: 'actions', headerName: 'Actions', width: 500, renderCell: (params: GridCellParams) =>
                <React.Fragment>
                    <Button component={Link} to={"/users/"+params.row.correspondentId+"?tab=chat&chatId="+params.row.chatId} variant="contained" style={{ height: '32px', width:'128px' }}>Chat</Button>&nbsp;
                    <Tooltip title="Betty's side of this chat in the AI hub: her tool calls, results, replies and summary">
                        <Button component={Link} to={"/ai/conversations/support-chat/" + params.row.chatId} variant="outlined" style={{ height: '32px' }}>AI</Button>
                    </Tooltip>&nbsp;
                    {
                        !params.row.read ? <Button variant="outlined" style={{ height: '32px' }} onClick={() => onMessageRead(params.row.correspondentId, params.row.chatId)}>Mark as read</Button>
                            : <div />
                    }

                </React.Fragment>
        },
    ];


    const fetchData = async (offset: number, limit: number, showOnlyPriority?: boolean, showOnlyActive?: boolean, categoryIds?: string[]) => {
        setState(prevState => ({ ...prevState, loaded: false }));
        const allCategoriesSelected = categoryIds && categoryIds.length === categories.length;
        let response = await getSupportChatsPaginated(offset, limit, showOnlyPriority, showOnlyActive, categoryIds, allCategoriesSelected);
        let chats = response?.content ?? [];
        let totalItems = 10_000;
        const totalPages = Math.ceil(totalItems / limit)
        const currentPage = totalItems > 0 ? Math.ceil((offset + limit) / limit) : 1;
        setState(prevState => ({ ...prevState, chats: chats, loaded: true, totalPages: totalPages, currentPage: currentPage }));
    }

    const onPageChanged = async (p: number) => {
        await fetchData((p - 1) * state.limit, state.limit, state.showOnlyPriority, state.showOnlyActive, selectedCategoryIds);
    }

    const onTogglePriorityFilter = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        setState(prevState => ({ ...prevState, showOnlyPriority: newValue, currentPage: 1 }));
        await fetchData(0, state.limit, newValue, state.showOnlyActive, selectedCategoryIds);
    }

    const onToggleActiveFilter = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        setState(prevState => ({ ...prevState, showOnlyActive: newValue, currentPage: 1 }));
        await fetchData(0, state.limit, state.showOnlyPriority, newValue, selectedCategoryIds);
    }

    const onCategoryFilterChange = async (event: any) => {
        const newCategoryIds = event.target.value as string[];
        
        // Handle "Select All" option
        if (newCategoryIds.includes('SELECT_ALL')) {
            const allIds = categories.map(c => c.id);
            setSelectedCategoryIds(allIds);
            setState(prevState => ({ ...prevState, currentPage: 1 }));
            await fetchData(0, state.limit, state.showOnlyPriority, state.showOnlyActive, allIds);
            return;
        }
        
        // Handle "Deselect All" option
        if (newCategoryIds.includes('DESELECT_ALL')) {
            setSelectedCategoryIds([]);
            setState(prevState => ({ ...prevState, currentPage: 1 }));
            await fetchData(0, state.limit, state.showOnlyPriority, state.showOnlyActive, []);
            return;
        }
        
        setSelectedCategoryIds(newCategoryIds);
        setState(prevState => ({ ...prevState, currentPage: 1 }));
        await fetchData(0, state.limit, state.showOnlyPriority, state.showOnlyActive, newCategoryIds);
    }

    const loadAIConfig = async () => {
        setConfigLoading(true);
        try {
            const config = await getAISupportConfiguration();
            setAiConfig(config);
        } catch (err) {
            console.error('Failed to load AI config:', err);
        } finally {
            setConfigLoading(false);
        }
    };

    const loadCategories = async () => {
        setCategoriesLoading(true);
        try {
            const categoriesData = await getSupportChatCategories();
            if (categoriesData) {
                setCategories(categoriesData);
                // By default, all categories are selected
                setSelectedCategoryIds(categoriesData.map(c => c.id));
            }
        } catch (err) {
            console.error('Failed to load categories:', err);
        } finally {
            setCategoriesLoading(false);
        }
    };

    React.useEffect(() => {
        const initializePage = async () => {
            await loadCategories();
            // fetchData will be called after categories are loaded
        };
        initializePage();
        loadAIConfig();
    }, []);

    // Fetch data when categories are loaded and selectedCategoryIds is set
    React.useEffect(() => {
        if (categories.length > 0 && selectedCategoryIds.length > 0) {
            fetchData(state.offset, state.limit, state.showOnlyPriority, state.showOnlyActive, selectedCategoryIds);
        }
    }, [categories]);

    const handleConfigModalOpen = () => {
        setConfigModalOpen(true);
    };

    const handleConfigModalClose = () => {
        setConfigModalOpen(false);
    };

    const handleConfigSaved = () => {
        // Reload AI config after saving
        loadAIConfig();
        console.log('AI Support configuration saved');
    };

    return (
        <div><Stack gap="50px 50px" sx={{ mx: 5, my: 5 }} justifyContent='center' alignItems='center'>
            <Typography variant="h3">Support</Typography>
            
            {/* AI Support Configuration Info */}
            {configLoading ? (
                <CircularProgress size={24} />
            ) : aiConfig && (
                <Paper 
                    elevation={2} 
                    sx={{ 
                        p: 3, 
                        width: '100%', 
                        maxWidth: '1000px',
                        backgroundColor: '#f5f5f5'
                    }}
                >
                    {/* Header Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                        <Avatar
                            src={aiConfig.supportUserAvatarUrl}
                            alt={aiConfig.supportUserName}
                            sx={{ width: 56, height: 56 }}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ mb: 0.5 }}>
                                {aiConfig.supportUserName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip 
                                    label={`Auto-Close: ${aiConfig.chatAutoCloseTimeoutInHours}h`} 
                                    size="small" 
                                    variant="outlined"
                                />
                                <Chip 
                                    label={`Timeout: ${aiConfig.responseTimeout}s`} 
                                    size="small" 
                                    variant="outlined"
                                />
                            </Box>
                        </Box>
                        <Button 
                            variant="outlined" 
                            color="primary" 
                            onClick={handleConfigModalOpen}
                        >
                            Configure
                        </Button>
                    </Box>

                    {/* Betty in the AI hub: replies, conversations, users, cost, tokens, tool calls */}
                    <Box sx={{ borderTop: '1px solid #ddd', pt: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                            AI Support Agent
                        </Typography>
                        <AiAgentStatsPanel agentCode="support.betty" />
                    </Box>
                </Paper>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Pagination count={state.totalPages} size="medium" onChange={(e, p) => onPageChanged(p)} page={state.currentPage} />
                <FormControl sx={{ minWidth: 250 }}>
                    <InputLabel id="category-filter-label">Categories</InputLabel>
                    <Select
                        labelId="category-filter-label"
                        id="category-filter"
                        multiple
                        value={selectedCategoryIds}
                        onChange={onCategoryFilterChange}
                        label="Categories"
                        renderValue={(selected) => {
                            if (selected.length === 0) {
                                return 'No categories selected';
                            }
                            if (selected.length === categories.length) {
                                return 'All Categories';
                            }
                            return categories
                                .filter(cat => selected.includes(cat.id))
                                .map(cat => cat.name)
                                .join(', ');
                        }}
                        disabled={categoriesLoading}
                    >
                        {/* Select All / Deselect All options */}
                        <MenuItem 
                            value="SELECT_ALL"
                            sx={{ 
                                borderBottom: '1px solid #ddd',
                                fontWeight: 'bold',
                                backgroundColor: '#f5f5f5',
                                '&:hover': {
                                    backgroundColor: '#e0e0e0'
                                }
                            }}
                        >
                            ✓ Select All
                        </MenuItem>
                        <MenuItem 
                            value="DESELECT_ALL"
                            sx={{ 
                                borderBottom: '2px solid #ddd',
                                fontWeight: 'bold',
                                backgroundColor: '#f5f5f5',
                                mb: 1,
                                '&:hover': {
                                    backgroundColor: '#e0e0e0'
                                }
                            }}
                        >
                            ✗ Deselect All
                        </MenuItem>
                        {/* Category options */}
                        {categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={selectedCategoryIds.includes(category.id)}
                                            color="primary"
                                        />
                                    }
                                    label={category.name}
                                />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={
                        <Switch
                            checked={state.showOnlyPriority}
                            onChange={onTogglePriorityFilter}
                            color="error"
                        />
                    }
                    label="Priority Only"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={state.showOnlyActive}
                            onChange={onToggleActiveFilter}
                            color="primary"
                        />
                    }
                    label="Active Only"
                />
            </Box>
            {!state.loaded ? <Grid item xs={2}><CircularProgress size='100px' style={{ marginBottom: '32px' }} /></Grid> : <DataGrid
                rows={state.chats}
                getRowId={(row) => row.chatId}
                columns={columns}
                initialState={{
                    pagination: {
                        paginationModel: { page: 0, pageSize: state.limit },
                    },
                }}
                isRowSelectable={(params) => false}
                pageSizeOptions={[5, 10]}
                hideFooterPagination
                getRowHeight={() => 'auto'}
                getRowClassName={(params) => {
                    // A closed ticket is always shown as closed (gray), whether its last message was read or not.
                    if (!params.row.active) return 'chatInactive';
                    return params.row.read ? 'messageRead' : 'messageNotRead';
                }}
                sx={{
                    width: '100%',
                    "& .messageRead": {
                        backgroundColor: "transparent",
                    },
                    "& .messageNotRead": {
                        backgroundColor: "lightGreen"
                    },
                    "& .chatInactive": {
                        backgroundColor: "#ececec",
                        fontStyle: "italic",
                        "&:hover, &.Mui-hovered": {
                            backgroundColor: "#e4e4e4",
                        },
                        "& .MuiDataGrid-cell": {
                            color: "#8a8a8a",
                        },
                        "& img, & .MuiAvatar-root": {
                            filter: "grayscale(1)",
                        },
                    },
                    "&.Mui-hovered": {
                        background: 'transparent',
                        backgroundColor: "transparent",
                    },
                }}
            />}
            <AISupportConfigModal 
                open={configModalOpen}
                onClose={handleConfigModalClose}
                onSave={handleConfigSaved}
            />
        </Stack >
        </div>
    );
}
